import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { apiService } from "../../services/apiService";
import { OAuthService } from "../../services/oauthService";
import { clearOnboardingData } from "../../utils/storage";
import LoadingIndicator from "../../components/LoadingIndicator";

// Session storage keys matching Signup.tsx
const INVITE_PROCESSING_KEY = "portfolio_invite_processing";
const INVITE_PROCESSED_KEY = "portfolio_invite_completed";

const AuthCallback: React.FC = () => {
  const { supabase } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const callbackHandledRef = useRef<boolean>(false);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Prevent duplicate handling in strict mode or fast refreshes
      if (callbackHandledRef.current) {
        console.log("[AuthCallback] Already handled, skipping");
        return;
      }
      callbackHandledRef.current = true;

      try {
        console.log("[AuthCallback] Starting to handle redirect");

        // Check for OAuth errors in URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const oauthError = urlParams.get("error");
        const errorDescription = urlParams.get("error_description");

        if (oauthError) {
          console.error(
            "[AuthCallback] OAuth error from URL:",
            oauthError,
            errorDescription
          );
          setError(
            errorDescription || "OAuth authentication failed. Please try again."
          );
          setTimeout(() => window.location.replace("/login"), 3000);
          return;
        }

        // Ensure Supabase consumes the URL fragment or code to establish the session
        let data: any;
        let authError: any = null;
        try {
          const anyAuth: any = supabase.auth as any;
          // Prefer parsing the URL first (works for email confirmations and OTP)
          if (typeof anyAuth.getSessionFromUrl === "function") {
            await anyAuth.getSessionFromUrl({ storeSession: true });
          } else if (typeof anyAuth.exchangeCodeForSession === "function") {
            await anyAuth.exchangeCodeForSession({ storeSession: true });
          }
        } catch (consumeErr) {
          console.warn("[AuthCallback] Failed to consume URL:", consumeErr);
        } finally {
          const res = await supabase.auth.getSession();
          data = res.data;
          authError = res.error as any;
        }

        if (authError) {
          console.error("[AuthCallback] Auth error:", authError);
          setError("Authentication failed. Please try again.");
          setTimeout(() => window.location.replace("/login"), 3000);
          return;
        }

        if (!data.session?.user) {
          console.log("[AuthCallback] No session found, redirecting to login");
          window.location.replace("/login");
          return;
        }

        const user = data.session.user;
        const session = data.session;
        console.log("[AuthCallback] User authenticated:", user.id);

        // Clear any stale onboarding data to avoid cross-account loops
        try {
          clearOnboardingData();
        } catch (err) {
          console.debug("[AuthCallback] clearOnboardingData failed:", err);
        }

        // CRITICAL: Capture OAuth tokens from session to create integration record
        try {
          console.log(
            "[AuthCallback] Attempting to capture OAuth tokens from session"
          );
          const tokensCaptured =
            await OAuthService.captureOAuthTokensFromSession(session);
          if (tokensCaptured) {
            console.log("[AuthCallback] Successfully captured OAuth tokens");
          } else {
            console.log(
              "[AuthCallback] No OAuth tokens found in session or capture failed"
            );
          }
        } catch (tokenError) {
          console.error(
            "[AuthCallback] Failed to capture OAuth tokens:",
            tokenError
          );
          // Don't block the flow, but log the error
        }

        // Restore pending promo code from OAuth flow
        const pendingPromo = sessionStorage.getItem("pending_promo_code");
        if (pendingPromo) {
          localStorage.setItem("promo_code", pendingPromo);
          sessionStorage.removeItem("pending_promo_code");
        }

        // Check for pending portfolio invite from OAuth flow
        const pendingInvite = sessionStorage.getItem("pending_portfolio_invite");
        const portfolioInviteData = localStorage.getItem("portfolio_invite_data");

        if (pendingInvite && portfolioInviteData) {
          console.log("[AuthCallback] Found pending portfolio invite:", pendingInvite);

          // Check if already processed
          const processedInvite = sessionStorage.getItem(INVITE_PROCESSED_KEY);
          if (processedInvite === pendingInvite) {
            console.log("[AuthCallback] Invite already processed, redirecting to dashboard");
            sessionStorage.removeItem("pending_portfolio_invite");
            window.location.replace("/");
            return;
          }

          try {
            // Parse the invite data to get the inviteId
            const inviteData = JSON.parse(portfolioInviteData);
            if (inviteData.inviteId) {
              console.log("[AuthCallback] Accepting portfolio invite:", inviteData.inviteId);
              await apiService.acceptPortfolioInvite(inviteData.inviteId);
              console.log("[AuthCallback] Portfolio invite accepted successfully");

              // Mark as completed
              sessionStorage.setItem(INVITE_PROCESSED_KEY, pendingInvite);
              sessionStorage.removeItem(INVITE_PROCESSING_KEY);
              sessionStorage.removeItem("pending_portfolio_invite");
            }
          } catch (inviteError) {
            console.error("[AuthCallback] Error accepting portfolio invite:", inviteError);
            // Clear the pending invite but don't block - user can retry from signup page
            sessionStorage.removeItem("pending_portfolio_invite");
          }
        }

        // Use window.location.replace for clean navigation without history entry
        // This prevents back button from returning to callback page (two-tabs issue)
        console.log("[AuthCallback] Redirecting to home with replace");
        window.location.replace("/");
      } catch (error) {
        console.error("[AuthCallback] Handling failed:", error);
        setError(
          "Something went wrong during authentication. Please try again."
        );
        setTimeout(() => window.location.replace("/login"), 3000);
      } finally {
        setLoading(false);
      }
    };

    handleOAuthCallback();
  }, [supabase]);

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <h2>Authentication Error</h2>
        <p>{error}</p>
        <p>Redirecting to login page...</p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <LoadingIndicator />
      <p style={{ marginTop: "20px" }}>Completing your sign-in...</p>
    </div>
  );
};

export default AuthCallback;
